import {gql} from '@apollo/client';
import {useCallback} from 'react';

import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';

import {
  AgentApprovalCategory,
  GqlMessageBlock,
  RoutineRunAs,
  RoutineStatus,
  TriggerType,
} from './RoutineTypes';

export const ROUTINE_QUERY = gql`
  query RoutineQuery($id: StrictString!) @engComponent(name: LLM) @skipAudit {
    routine(id: $id) {
      id
      name
      description
      messageBlocks {
        ... on DisplayTextBlockNode {
          text
          displayType
        }
        ... on DisplayFileBlockNode {
          externalFileId
          filename
          mediaType
        }
      }
      agentType
      triggerType
      triggerConfig
      status
      allowedApprovalCategories
      runAs
      memoryEnabled
      agentWorkspaceFolder {
        id
      }
      createdAt
      modifiedAt
      creatorId
      archiveData {
        archived
      }
      permissions {
        archivable
        writable
      }
    }
  }
`;

export interface RoutineQueryData {
  routine: {
    id: string;
    name: string;
    description: string | null;
    messageBlocks: Array<GqlMessageBlock>;
    agentType: string;
    triggerType: TriggerType;
    triggerConfig: Record<string, unknown>;
    status: RoutineStatus;
    allowedApprovalCategories: Array<AgentApprovalCategory>;
    runAs: RoutineRunAs;
    memoryEnabled: boolean;
    agentWorkspaceFolder: {id: string} | null;
    createdAt: string;
    modifiedAt: string;
    creatorId: string | null;
    archiveData: {
      archived: boolean;
    };
    permissions: {
      archivable: boolean;
      writable: boolean;
    };
  } | null;
}

interface UseRoutineResult {
  routine: RoutineQueryData['routine'];
  isLoading: boolean;
  isError: boolean;
  refetch: () => Promise<void>;
}

export default function useRoutine({
  routineId,
  enabled = true,
}: {
  routineId: string | null;
  enabled?: boolean;
}): UseRoutineResult {
  const queryResult = useSkippableBenchlingQuery<RoutineQueryData>(ROUTINE_QUERY, {
    skip: !enabled || routineId == null,
    variables: {id: routineId ?? ''},
    fetchPolicy: 'network-only',
  });

  const refetch = useCallback(async () => {
    if (queryResult.type === QueryResultType.SUCCESS) {
      await queryResult.refetch();
    }
  }, [queryResult]);

  return {
    routine: queryResult.type === QueryResultType.SUCCESS ? queryResult.data.routine : null,
    isLoading: enabled && routineId != null && queryResult.type === QueryResultType.PENDING,
    isError: queryResult.type === QueryResultType.ERROR,
    refetch,
  };
}
